import React, { useState, useEffect } from 'react';
import { categoriesAPI } from '../services/api';
import CategoryCard from './CategoryCard';
import './CategoryGrid.css';

const CategoryGrid = ({ 
  title = "Categories", 
  showTitle = true,
  layout = 'grid', // 'grid' or 'list'
  size = 'medium', // 'small', 'medium', 'large'
  showDescription = true,
  showSubCategories = false,
  featured = false,
  parentId = null,
  level = null,
  limit = null,
  className = '',
  onCategorySelect = null
}) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, [featured, parentId, level, limit]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      const params = {};

      if (featured) {
        response = await categoriesAPI.getFeaturedCategories();
      } else if (parentId !== null) {
        if (parentId === 'root') {
          response = await categoriesAPI.getRootCategories();
        } else {
          response = await categoriesAPI.getSubCategories(parentId);
        }
      } else if (level !== null) {
        response = await categoriesAPI.getCategoriesByLevel(level);
      } else {
        if (limit) {
          params.per_page = limit;
        }
        response = await categoriesAPI.getCategories(params);
      }

      if (response.success) {
        let categoriesData = response.data;
        
        // If it's paginated data, extract the data array
        if (categoriesData.data) {
          categoriesData = categoriesData.data;
        }
        
        setCategories(categoriesData);
      } else {
        setError(response.message || 'Failed to fetch categories');
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderLoading = () => (
    <div className="category-grid-loading">
      <div className="loading-spinner"></div>
      <p>Loading categories...</p>
    </div>
  );

  const renderError = () => (
    <div className="category-grid-error">
      <i className="bi bi-exclamation-triangle"></i>
      <p>{error}</p>
      <button onClick={fetchCategories} className="retry-button">
        Try Again
      </button>
    </div>
  );

  const renderEmpty = () => (
    <div className="category-grid-empty">
      <i className="bi bi-tags"></i>
      <h3>No categories found</h3>
      <p>There are no categories available at the moment.</p>
    </div>
  );

  const getGridClass = () => {
    let classes = ['category-grid', `layout-${layout}`, `size-${size}`];
    if (className) classes.push(className);
    return classes.join(' ');
  };

  const getCardClass = () => {
    return `grid-${size}`;
  };

  if (loading) return renderLoading();
  if (error) return renderError();
  if (categories.length === 0) return renderEmpty();

  return (
    <div className={getGridClass()}>
      {showTitle && (
        <div className="category-grid-header">
          <h2 className="category-grid-title">{title}</h2>
          {featured && (
            <span className="featured-badge">
              <i className="bi bi-star-fill"></i>
              Featured
            </span>
          )}
        </div>
      )}
      
      <div className="category-grid-content">
        {categories.map((category) => (
          <CategoryCard
            key={category.id}
            category={category}
            showDescription={showDescription}
            showSubCategories={showSubCategories}
            className={getCardClass()}
            onCategorySelect={onCategorySelect}
          />
        ))}
      </div>
    </div>
  );
};

export default CategoryGrid;
